import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import type { ElementPropertiesPanelRef } from "./ElementPropertiesPanel";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { Button } from "../ui/Button";
import { Heading } from "../ui/Heading";
import { Modal } from "../ui/Modal";
import { DesignElement } from "./types";
import { productCustomizationApi } from "../../services/productCustomizationApi";
import { customizationAssetApi } from "../../services/customizationAssetApi";
import DesignCanvas from "./DesignCanvas";
import ComponentPalette from "./ComponentPalette";
import LayerTree from "./LayerTree";
import ElementPropertiesPanel from "./ElementPropertiesPanel";
import Product3DPreview from "./Product3DPreview";
import {
  FaEyeSlash,
  FaSave,
  FaTimes,
  FaDownload,
  FaExclamationCircle,
  FaInfoCircle,
  FaCube,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";

export interface PrintDesignerProps {
  productId: string;
  baseImage?: string;
  canvasSize?: string;
  onSave?: (customizationId: string) => void;
  onCancel?: () => void;
  initialElements?: DesignElement[];
  initialBackgroundColor?: string;
  initialBackgroundImage?: string;
  draftCustomizationId?: string;
  backgroundImages?: string[]; // Background images from product
  availableShapes?: Array<{
    id: string;
    name: string;
    url: string;
    category?: string;
  }>; // Available shapes from product
  allowedDesignerFonts?: string[]; // Allowed fonts for this product (if empty/null, uses store-level or all fonts)
  onDraftSaved?: (customizationId: string) => void; // Callback when draft is saved
  onCustomizationUpdated?: (customizationId: string) => void; // Callback when customization is updated (for refreshing order data)
}

export const PrintDesigner: React.FC<PrintDesignerProps> = ({
  productId,
  baseImage: _baseImage,
  canvasSize: initialCanvasSize = "mug",
  onSave,
  onCancel,
  initialElements = [],
  initialBackgroundColor = "#FFFFFF",
  initialBackgroundImage,
  draftCustomizationId,
  backgroundImages = [],
  availableShapes = [],
  allowedDesignerFonts,
  onDraftSaved,
  onCustomizationUpdated,
}) => {
  const { user } = useAuth();
  // Use a ref to track the previous initialElements to prevent unnecessary updates
  const prevInitialElementsKeyRef = useRef<string>("");
  // Track if we've already loaded the draft to prevent infinite loops
  const draftLoadedRef = useRef<string | null>(null);

  // Helper function to transform elements with IDs
  const transformElements = useCallback(
    (els: DesignElement[]): DesignElement[] => {
      return els.map((el, index) => ({
        ...el,
        id:
          el.id ||
          `element-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
      }));
    },
    []
  );

  // Initialize elements state with transformed initial elements
  const [elements, setElements] = useState<DesignElement[]>(() => {
    return transformElements(initialElements);
  });
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [selectedCanvas, setSelectedCanvas] = useState(false); // Track if canvas is selected
  const [saving, setSaving] = useState(false);
  // Canvas size is now read-only from product, use prop directly
  const canvasSize = initialCanvasSize;
  const [backgroundImage, setBackgroundImage] = useState<string | undefined>(
    initialBackgroundImage
  );
  const [backgroundColor, setBackgroundColor] = useState<string>(
    initialBackgroundColor
  );
  const [backgroundAssets, setBackgroundAssets] = useState<Map<string, string>>(
    new Map()
  ); // Map of URL -> originalUrl
  const backgroundImageManuallyChangedRef = useRef<boolean>(false); // Track if user manually changed background image
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [activeTab, setActiveTab] = useState<"components" | "layers">(
    "components"
  );
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const propertiesPanelRef = useRef<ElementPropertiesPanelRef>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Store baseline state for comparison (updated after initialization and draft loading)
  const baselineStateRef = useRef<{
    elements: Array<Omit<DesignElement, "id">>;
    backgroundColor: string;
    backgroundImage?: string;
  } | null>(null);

  // Store initial elements for comparison (without IDs for stable comparison)
  const initialElementsForComparison = useMemo(() => {
    return initialElements.map(({ id, ...el }) => el);
  }, [initialElements]);

  // Load draft customization when draftCustomizationId is provided
  const loadDraftCustomization = useCallback(async () => {
    if (!draftCustomizationId) return;

    // Prevent loading the same draft multiple times
    if (draftLoadedRef.current === draftCustomizationId) return;

    try {
      draftLoadedRef.current = draftCustomizationId;
      const customization =
        await productCustomizationApi.getCustomizationById(
          draftCustomizationId
        );
      if (customization) {
        // Transform and set elements
        let transformed: DesignElement[] = [];
        if (customization.elements && Array.isArray(customization.elements)) {
          transformed = transformElements(
            customization.elements as DesignElement[]
          );
          setElements(transformed);
        }
        // Set background
        // For admins viewing orders, initialBackgroundImage is already converted to original URL
        // Prefer initialBackgroundImage if provided (for admins), otherwise use customization's backgroundImage
        const bgColor = customization.backgroundColor || initialBackgroundColor;
        // Check current backgroundImage state - if it's already set to initialBackgroundImage (original URL), don't override it
        // This prevents race condition where loadDraftCustomization runs after useEffect has set the correct original URL
        const currentBgImage = backgroundImage;
        // If initialBackgroundImage is available, always use it (for admins, this is the original URL)
        // If not available yet, use customization's backgroundImage, but only if currentBgImage is not already set to a non-watermarked URL
        const shouldUseInitial = initialBackgroundImage !== undefined;
        // If current background is already the original URL (matches initialBackgroundImage), don't change it
        const isAlreadyOriginal =
          currentBgImage === initialBackgroundImage &&
          initialBackgroundImage !== undefined;
        const bgImage = shouldUseInitial
          ? initialBackgroundImage
          : isAlreadyOriginal
            ? currentBgImage
            : customization.backgroundImage || undefined;
        if (customization.backgroundColor) {
          setBackgroundColor(bgColor);
        }
        // Only set background image if it's different from current, or if we need to set it for the first time
        // This prevents overwriting the original URL with the watermarked one
        if (bgImage !== currentBgImage) {
          setBackgroundImage(bgImage);
        }
        // Set baseline after loading draft
        baselineStateRef.current = {
          elements: transformed.map(({ id, ...el }) => el),
          backgroundColor: bgColor,
          backgroundImage: bgImage,
        };
      }
    } catch (error) {
      console.error("Error loading draft customization:", error);
      // Reset the ref on error so we can retry
      draftLoadedRef.current = null;
    }
  }, [
    draftCustomizationId,
    transformElements,
    initialBackgroundColor,
    initialBackgroundImage,
  ]);

  useEffect(() => {
    if (draftCustomizationId) {
      // Reset the loaded ref when draftCustomizationId changes to allow reloading
      if (draftLoadedRef.current !== draftCustomizationId) {
        draftLoadedRef.current = null;
      }
      loadDraftCustomization();
    } else {
      // Reset ref when no draft
      draftLoadedRef.current = null;
      // Set baseline after initial mount when no draft is being loaded
      baselineStateRef.current = {
        elements: initialElementsForComparison,
        backgroundColor: initialBackgroundColor,
        backgroundImage: initialBackgroundImage,
      };
    }
    // Only depend on draftCustomizationId to prevent infinite loops
    // The loadDraftCustomization function has guards to prevent multiple loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftCustomizationId]);

  // Update background image when initialBackgroundImage prop changes
  // For draft customizations, we still want to update if the prop changes (e.g., when admin opens with original URL)
  // This is important because initialBackgroundImage might be updated after the draft is loaded (when assets are fetched)
  // BUT: Don't overwrite if user has manually changed the background image (e.g., via "Remove Watermark" button)
  useEffect(() => {
    if (
      initialBackgroundImage !== undefined &&
      !backgroundImageManuallyChangedRef.current
    ) {
      // Update background image from prop, even if draftCustomizationId is present
      // This allows admins to see original (unwatermarked) versions even when loading drafts
      setBackgroundImage(initialBackgroundImage);
    }
  }, [initialBackgroundImage, draftCustomizationId, backgroundImage]);

  // Auto-select canvas layer on component mount
  useEffect(() => {
    setSelectedCanvas(true);
    setSelectedElement(null);
  }, []);

  // Fetch background assets to get originalUrl mapping for admins
  useEffect(() => {
    const fetchAssets = async () => {
      if (backgroundImages.length === 0) {
        return;
      }

      try {
        const assets = await customizationAssetApi.getAssets("background");
        const urlMap = new Map<string, string>();

        // Helper to extract filename from URL for matching
        const getFilename = (url: string): string => {
          try {
            const urlObj = new URL(url);
            return urlObj.pathname.split("/").pop() || "";
          } catch {
            return url.split("/").pop() || "";
          }
        };

        // Create mapping of any URL (watermarked or original) to originalUrl
        // For admins, asset.url is the original URL, and asset.watermarkedUrl (if present) is the watermarked URL
        // For users, asset.url is the watermarked URL
        assets.forEach((asset) => {
          // For admins, url is the original, watermarkedUrl is the watermarked
          // For users, url is the watermarked (and there's no originalUrl in response)
          const originalUrl = asset.url; // For admins, this is already the original
          const watermarkedUrl = (asset as any).watermarkedUrl || asset.url; // Use watermarkedUrl if available, otherwise url (for users)

          // Map watermarked URL to original URL
          urlMap.set(watermarkedUrl, originalUrl);
          urlMap.set(originalUrl, originalUrl); // Original maps to itself

          // Also map by filename for better matching
          const watermarkedFilename = getFilename(watermarkedUrl);
          const originalFilename = getFilename(originalUrl);
          if (watermarkedFilename) urlMap.set(watermarkedFilename, originalUrl);
          if (originalFilename) urlMap.set(originalFilename, originalUrl);
        });

        setBackgroundAssets(urlMap);
      } catch (error) {
        console.warn("Failed to fetch background assets:", error);
      }
    };

    fetchAssets();
  }, [backgroundImages.length]);

  // Helper to get original URL for a background image (for admins)
  const getOriginalBackgroundUrl = useCallback(
    (url: string | undefined): string | undefined => {
      if (!url) return undefined;

      const isAdmin =
        user && (user.role === "admin" || user.role === "store_admin" || user.role === "co_admin");
      if (!isAdmin) {
        return url; // Regular users get watermarked version
      }

      // For admins, try to get originalUrl from the map
      const originalUrl = backgroundAssets.get(url);
      if (originalUrl) {
        return originalUrl;
      }

      // If not found in map, check by filename
      const getFilename = (urlStr: string): string => {
        try {
          const urlObj = new URL(urlStr);
          return urlObj.pathname.split("/").pop() || "";
        } catch {
          return urlStr.split("/").pop() || "";
        }
      };

      const filename = getFilename(url);
      const originalByFilename = backgroundAssets.get(filename);
      if (originalByFilename) {
        return originalByFilename;
      }

      // If no mapping found, assume url is already original (for admins, backend returns original)
      return url;
    },
    [user, backgroundAssets]
  );

  // Update elements when initialElements change (e.g., when loading a draft)
  // Only update if the actual content changed, not just the reference
  // Skip if we're loading from draftCustomizationId
  useEffect(() => {
    if (draftCustomizationId) return; // Don't update from initialElements if loading from draft

    // Create a stable key by stringifying the elements without IDs
    const currentKey = JSON.stringify(initialElementsForComparison);
    if (prevInitialElementsKeyRef.current !== currentKey) {
      const transformed = transformElements(initialElements);
      setElements(transformed);
      prevInitialElementsKeyRef.current = currentKey;
    }
  }, [
    initialElements,
    initialElementsForComparison,
    transformElements,
    draftCustomizationId,
  ]);

  // Track unsaved changes - compare current state with baseline state
  useEffect(() => {
    // Skip comparison if baseline hasn't been set yet
    if (!baselineStateRef.current) {
      setHasUnsavedChanges(false);
      return;
    }

    const baseline = baselineStateRef.current;

    // Compare elements (deep comparison - don't compare IDs as they may be regenerated)
    const elementsChanged =
      elements.length !== baseline.elements.length ||
      elements.some((el, idx) => {
        const baselineEl = baseline.elements[idx];
        if (!baselineEl) return true;
        // Compare by content, position, size, style, and zIndex (not ID)
        return (
          el.type !== baselineEl.type ||
          el.content !== baselineEl.content ||
          Math.abs(el.position.x - baselineEl.position.x) > 0.1 ||
          Math.abs(el.position.y - baselineEl.position.y) > 0.1 ||
          Math.abs(el.size.width - baselineEl.size.width) > 0.1 ||
          Math.abs(el.size.height - baselineEl.size.height) > 0.1 ||
          JSON.stringify(el.style) !== JSON.stringify(baselineEl.style) ||
          el.zIndex !== baselineEl.zIndex
        );
      });
    // Compare background
    const backgroundChanged =
      backgroundColor !== baseline.backgroundColor ||
      backgroundImage !== baseline.backgroundImage;

    const hasChanges = elementsChanged || backgroundChanged;
    setHasUnsavedChanges(hasChanges);
  }, [elements, backgroundColor, backgroundImage]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Helper function to get the first available shape URL from assets library
  const getFirstAvailableShape = useCallback((): string => {
    if (availableShapes.length > 0) {
      return availableShapes[0].url;
    }
    // Return empty string if no shapes are available from assets library
    return "";
  }, [availableShapes]);
  
  // Canvas size presets (matching DesignCanvas)
  const canvasSizes: Record<string, { width: number; height: number }> = {
    mug: { width: 800, height: 600 },
    bottle: { width: 600, height: 800 },
    tshirt: { width: 1000, height: 1200 },
    poster: { width: 1200, height: 1800 },
  };
  
  // Calculate appropriate font size based on canvas dimensions
  // For high-res images, use a percentage of canvas size to ensure visibility
  const calculateFontSize = useCallback((): number => {
    // Try to get actual canvas dimensions from the canvas element if available
    let canvasWidth: number;
    let canvasHeight: number;
    
    if (canvasRef.current) {
      const canvasElement = canvasRef.current;
      // Try to get actual dimensions from the background canvas
      const bgCanvas = canvasElement.querySelector("canvas") as HTMLCanvasElement;
      if (bgCanvas && bgCanvas.width > 0 && bgCanvas.height > 0) {
        canvasWidth = bgCanvas.width;
        canvasHeight = bgCanvas.height;
      } else {
        // Use preset dimensions
        const presetSize = canvasSizes[canvasSize] || canvasSizes.mug;
        canvasWidth = presetSize.width;
        canvasHeight = presetSize.height;
      }
    } else {
      // Fallback to preset dimensions
      const presetSize = canvasSizes[canvasSize] || canvasSizes.mug;
      canvasWidth = presetSize.width;
      canvasHeight = presetSize.height;
    }
    
    // Calculate font size as ~3-4% of average dimension for high-res images
    // Use average of width and height to handle both landscape and portrait orientations
    const avgDimension = (canvasWidth + canvasHeight) / 2;
    
    // For high-res images (>1500px average), use a smaller percentage to keep size reasonable
    // For smaller images, use a larger percentage
    let percentage = 0.03; // 3% default
    if (avgDimension > 1500) {
      percentage = 0.025; // 2.5% for very large images
    } else if (avgDimension < 700) {
      percentage = 0.04; // 4% for smaller images
    }
    
    const calculatedSize = Math.round(avgDimension * percentage);
    
    // Clamp to reasonable bounds (min 16px, max 300px)
    return Math.max(16, Math.min(300, calculatedSize));
  }, [canvasSize]);

  const addElement = useCallback(
    (type: "text" | "shape" | "frame", content?: string) => {
      // For shape elements, use provided content or default to first available shape
      let shapeContent = content;
      if (type === "shape" && !shapeContent) {
        shapeContent = getFirstAvailableShape();
      }

      const newElement: DesignElement = {
        id: `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type,
        content: shapeContent || (type === "text" ? "New Text" : ""),
        position: { x: 100, y: 100 },
        size: {
          width: type === "text" ? 200 : 150,
          height: type === "text" ? 50 : 150,
        },
        style: {
          fontFamily: "Arial",
          fontSize: type === "text" ? calculateFontSize() : undefined,
          color: type === "text" ? "#000000" : undefined,
          backgroundColor: type === "shape" ? "#000000" : undefined,
          borderColor: type === "frame" ? "#000000" : undefined,
          borderWidth: type === "frame" ? 2 : undefined,
          borderRadius: 0,
          rotation: 0,
          opacity: 1,
        },
        zIndex: elements.length,
      };

      setElements([...elements, newElement]);
      setSelectedElement(newElement.id);
      setSelectedCanvas(false); // Clear canvas selection when element is selected
    },
    [elements, getFirstAvailableShape]
  );

  const updateElement = useCallback(
    (id: string, updates: Partial<DesignElement>) => {
      setElements((prev) =>
        prev.map((el) => (el.id === id ? { ...el, ...updates } : el))
      );
    },
    []
  );

  const deleteElement = useCallback(
    (id: string) => {
      setElements((prev) => prev.filter((el) => el.id !== id));
      if (selectedElement === id) {
        setSelectedElement(null);
      }
    },
    [selectedElement]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveDragId(null);

      if (!over) {
        return;
      }

      // Check if dropping on canvas
      if (over.id === "canvas-drop-zone") {
        const dragData = active.data.current;
        if (dragData?.type) {
          // Add new element at drop position
          const rect = canvasRef.current?.getBoundingClientRect();
          if (rect && event.activatorEvent) {
            const mouseEvent = event.activatorEvent as MouseEvent;
            const displayX = mouseEvent.clientX - rect.left;
            const displayY = mouseEvent.clientY - rect.top;

            // Convert display coordinates to logical canvas coordinates (preset size)
            // Elements must be stored in the preset canvas coordinate space for 3D viewer
            const presetSize = canvasSizes[canvasSize] || canvasSizes.mug;
            
            // Calculate baseDisplayScale (same logic as DesignCanvas)
            // Get parent width from canvas ref's parent element
            const canvasElement = canvasRef.current;
            const parentElement = canvasElement?.parentElement;
            const parentWidth = parentElement?.getBoundingClientRect().width || (typeof window !== "undefined" ? window.innerWidth - 600 : 1200);
            const baseDisplayScale = Math.min(parentWidth / presetSize.width, 1);
            
            // Get zoom level from canvas element's transform style
            let zoomLevel = 1;
            if (canvasElement) {
              const canvasBox = canvasElement.querySelector("[style*='transform']") as HTMLElement;
              if (canvasBox) {
                const transform = canvasBox.style.transform;
                const match = transform.match(/scale\(([^)]+)\)/);
                if (match) {
                  zoomLevel = parseFloat(match[1]) || 1;
                }
              }
            }
            
            // Convert display coordinates to logical coordinates
            // Logical coordinates are in the preset canvas size space (e.g., 800x600)
            const logicalX = (displayX / zoomLevel) / baseDisplayScale;
            const logicalY = (displayY / zoomLevel) / baseDisplayScale;
            
            // Offset by half element size for better centering
            const offsetX = dragData.type === "text" ? 100 : 75;
            const offsetY = dragData.type === "text" ? 25 : 75;
            
            const x = Math.max(0, logicalX - offsetX);
            const y = Math.max(0, logicalY - offsetY);

            // Determine content based on element type
            let elementContent: string;
            if (dragData.type === "text") {
              elementContent = dragData.content || "New Text";
            } else if (dragData.type === "shape") {
              elementContent = dragData.content || getFirstAvailableShape();
            } else {
              elementContent = dragData.content || "";
            }

            const newElement: DesignElement = {
              id: `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              type: dragData.type as "text" | "shape" | "frame",
              content: elementContent,
              position: { x, y },
              size: {
                width: dragData.type === "text" ? 200 : 150,
                height: dragData.type === "text" ? 50 : 150,
              },
              style: {
                fontFamily: "Arial",
                fontSize: dragData.type === "text" ? calculateFontSize() : undefined,
                color: dragData.type === "text" ? "#000000" : undefined,
                backgroundColor:
                  dragData.type === "shape" ? "#000000" : undefined,
                borderColor: dragData.type === "frame" ? "#000000" : undefined,
                borderWidth: dragData.type === "frame" ? 2 : undefined,
                borderRadius: 0,
                rotation: 0,
                opacity: 1,
              },
              zIndex: elements.length,
            };
            setElements([...elements, newElement]);
            setSelectedElement(newElement.id);
            setSelectedCanvas(false); // Clear canvas selection when element is selected
          } else {
            // Fallback: add at default position
            addElement(
              dragData.type as "text" | "shape" | "frame",
              dragData.content
            );
          }
        }
        return;
      }

      // Handle reordering elements in layers
      const oldIndex = elements.findIndex((el) => el.id === active.id);
      const newIndex = elements.findIndex((el) => el.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newElements = arrayMove(elements, oldIndex, newIndex);
        // Update zIndex based on new order
        const updatedElements = newElements.map((el, index) => ({
          ...el,
          zIndex: index,
        }));
        setElements(updatedElements);
      }
    },
    [elements, addElement, getFirstAvailableShape, canvasSize]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  }, []);

  const saveDraft = useCallback(async () => {
    try {
      const customizationData = {
        productId,
        customizationType: "2d-print" as const,
        elements: elements.map((el) => ({
          type: el.type,
          content: el.content,
          position: el.position,
          size: el.size,
          style: el.style,
          zIndex: el.zIndex,
        })),
        canvasSize,
        backgroundColor,
        backgroundImage,
        isDraft: true,
      };

      let customization;
      if (draftCustomizationId) {
        // Update existing draft
        customization = await productCustomizationApi.update(
          draftCustomizationId,
          customizationData
        );
      } else {
        // Create new draft
        customization = await productCustomizationApi.create(customizationData);
      }

      // Update baseline after saving
      baselineStateRef.current = {
        elements: elements.map(({ id, ...el }) => el),
        backgroundColor,
        backgroundImage,
      };

      // Notify parent that draft was saved
      if (customization._id) {
        if (onDraftSaved) {
          onDraftSaved(customization._id);
        }
        // Notify that customization was updated (for refreshing order data)
        if (onCustomizationUpdated) {
          onCustomizationUpdated(customization._id);
        }
      }
    } catch (error: any) {
      console.error("Error saving draft:", error);
      // Don't show alert for draft saves - fail silently
    }
  }, [
    elements,
    productId,
    canvasSize,
    backgroundColor,
    backgroundImage,
    draftCustomizationId,
    onDraftSaved,
    onCustomizationUpdated,
  ]);

  const handleSave = useCallback(async () => {
    if (elements.length === 0) {
      return;
    }

    setSaving(true);
    try {
      const customizationData = {
        productId,
        customizationType: "2d-print" as const,
        elements: elements.map((el) => ({
          type: el.type,
          content: el.content,
          position: el.position,
          size: el.size,
          style: el.style,
          zIndex: el.zIndex,
        })),
        canvasSize,
        backgroundColor,
        backgroundImage,
        isDraft: true, // Keep as draft so it can be continued later
      };

      let customization;
      if (draftCustomizationId) {
        // Update existing draft
        customization = await productCustomizationApi.update(
          draftCustomizationId,
          customizationData
        );
      } else {
        // Create new draft
        customization = await productCustomizationApi.create(customizationData);
      }

      // Update the draft ID so it can be continued later
      if (customization._id) {
        // Update baseline after saving
        baselineStateRef.current = {
          elements: elements.map(({ id, ...el }) => el),
          backgroundColor,
          backgroundImage,
        };

        // Notify parent that customization was saved
        if (onSave) {
          onSave(customization._id);
        }
        // Also notify about draft update
        if (onDraftSaved) {
          onDraftSaved(customization._id);
        }
        // Notify that customization was updated (for refreshing order data)
        if (onCustomizationUpdated) {
          onCustomizationUpdated(customization._id);
        }
      }
    } catch (error: any) {
      console.error("Error saving customization:", error);
      alert(`Failed to save customization: ${error.message}`);
    } finally {
      setSaving(false);
    }
  }, [
    elements,
    productId,
    canvasSize,
    backgroundColor,
    backgroundImage,
    draftCustomizationId,
    onSave,
    onDraftSaved,
    onCustomizationUpdated,
  ]);

  // Handle cancel with confirmation if there are unsaved changes
  const handleCancel = useCallback(() => {
    // Skip confirmation if baseline hasn't been set yet (shouldn't happen, but safety check)
    if (!baselineStateRef.current) {
      if (onCancel) {
        onCancel();
      }
      return;
    }

    const baseline = baselineStateRef.current;

    // Always check for changes when closing (in case the state hasn't updated yet)
    const hasChanges =
      elements.length !== baseline.elements.length ||
      elements.some((el, idx) => {
        const baselineEl = baseline.elements[idx];
        if (!baselineEl) return true;
        return (
          el.type !== baselineEl.type ||
          el.content !== baselineEl.content ||
          Math.abs(el.position.x - baselineEl.position.x) > 0.1 ||
          Math.abs(el.position.y - baselineEl.position.y) > 0.1 ||
          Math.abs(el.size.width - baselineEl.size.width) > 0.1 ||
          Math.abs(el.size.height - baselineEl.size.height) > 0.1 ||
          JSON.stringify(el.style) !== JSON.stringify(baselineEl.style) ||
          el.zIndex !== baselineEl.zIndex
        );
      }) ||
      backgroundColor !== baseline.backgroundColor ||
      backgroundImage !== baseline.backgroundImage;

    if (hasChanges) {
      setShowCloseConfirm(true);
    } else {
      if (onCancel) {
        onCancel();
      }
    }
  }, [elements, backgroundColor, backgroundImage, onCancel]);

  // Save and close
  const handleSaveAndClose = useCallback(async () => {
    if (hasUnsavedChanges) {
      await saveDraft();
    }
    setShowCloseConfirm(false);
    if (onCancel) {
      onCancel();
    }
  }, [hasUnsavedChanges, saveDraft, onCancel]);

  // Close without saving
  const handleCloseWithoutSaving = useCallback(() => {
    setShowCloseConfirm(false);
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  const selectedElementData = elements.find((el) => el.id === selectedElement);

  const handleElementSelect = (id: string | null) => {
    setSelectedElement(id);
    setSelectedCanvas(false);
  };

  const handleCanvasSelect = () => {
    setSelectedCanvas(true);
    setSelectedElement(null);
  };

  const handleElementDoubleClick = useCallback((id: string) => {
    // Select the element first
    setSelectedElement(id);
    setSelectedCanvas(false);
    // Then focus the text input after a short delay to ensure the panel is rendered
    setTimeout(() => {
      if (propertiesPanelRef.current) {
        propertiesPanelRef.current.focusTextInput();
      }
    }, 100);
  }, []);

  // Export canvas as PNG - loads images with CORS for export
  const handleExportPNG = useCallback(async () => {
    if (elements.length === 0) {
      alert("No design to export");
      return;
    }

    if (!canvasRef.current) {
      alert("Canvas not found");
      return;
    }

    setExporting(true);
    try {
      // Canvas size presets
      const canvasSizes: Record<string, { width: number; height: number }> = {
        mug: { width: 800, height: 600 },
        bottle: { width: 600, height: 800 },
        tshirt: { width: 1000, height: 1200 },
        poster: { width: 1200, height: 1800 },
      };

      // Get actual canvas dimensions (not the scaled display size)
      // For admins, use original (non-watermarked) background URL
      const isAdmin =
        user && (user.role === "admin" || user.role === "store_admin" || user.role === "co_admin");
      const backgroundImageForExport = isAdmin
        ? getOriginalBackgroundUrl(backgroundImage)
        : backgroundImage;

      let actualWidth: number;
      let actualHeight: number;

      if (backgroundImageForExport) {
        // Always fetch image via backend API to get dimensions (bypasses CORS)
        try {
          const dataURL = await productCustomizationApi.fetchImageAsDataURL(
            backgroundImageForExport
          );
          const img = new Image();
          const dimensions = await new Promise<{
            width: number;
            height: number;
          }>((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error("Background image load timeout"));
            }, 10000);

            img.onload = () => {
              clearTimeout(timeout);
              if (img.naturalWidth === 0 || img.naturalHeight === 0) {
                reject(new Error("Invalid image dimensions"));
              } else {
                resolve({
                  width: img.naturalWidth,
                  height: img.naturalHeight,
                });
              }
            };
            img.onerror = () => {
              clearTimeout(timeout);
              reject(
                new Error(
                  `Failed to load background image: ${backgroundImageForExport}`
                )
              );
            };
            img.src = dataURL;
          });
          actualWidth = dimensions.width;
          actualHeight = dimensions.height;
        } catch (error: any) {
          // If background image fails to load, use preset dimensions
          console.warn(
            "Failed to load background image for dimensions, using preset:",
            error?.message
          );
          const size = canvasSizes[canvasSize] || canvasSizes.mug;
          actualWidth = size.width;
          actualHeight = size.height;
        }
      } else {
        // Use preset dimensions
        const size = canvasSizes[canvasSize] || canvasSizes.mug;
        actualWidth = size.width;
        actualHeight = size.height;
      }

      // Elements are stored in preset canvas coordinates, so we need to scale from preset to actual image size
      // Scale independently on X and Y to match actual image dimensions
      const presetSize = canvasSizes[canvasSize] || canvasSizes.mug;
      const scaleX = actualWidth / presetSize.width;
      const scaleY = actualHeight / presetSize.height;

      // Get the canvas element for accessing DOM elements
      const canvasElement = canvasRef.current;

      // Create export canvas at original image dimensions
      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = actualWidth;
      exportCanvas.height = actualHeight;
      const ctx = exportCanvas.getContext("2d", {
        alpha: true,
        desynchronized: false,
        willReadFrequently: false,
      });

      if (!ctx) {
        throw new Error("Could not get canvas context");
      }

      // Enable high-quality image rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      // Draw background color
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, actualWidth, actualHeight);

      // Draw background image if present (fetch via backend to bypass CORS)
      // For admin users, use original unwatermarked version for export
      if (backgroundImageForExport) {
        try {
          const dataURL = await productCustomizationApi.fetchImageAsDataURL(
            backgroundImageForExport
          );
          const bgImg = new Image();
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error("Background image load timeout"));
            }, 10000);
            bgImg.onload = () => {
              clearTimeout(timeout);
              try {
                // Use full natural dimensions for best quality
                const imgWidth = bgImg.naturalWidth;
                const imgHeight = bgImg.naturalHeight;
                // Draw at full resolution, scaling to fit canvas if needed
                ctx.drawImage(
                  bgImg,
                  0,
                  0,
                  imgWidth,
                  imgHeight,
                  0,
                  0,
                  actualWidth,
                  actualHeight
                );
                resolve();
              } catch (err: any) {
                reject(err);
              }
            };
            bgImg.onerror = () => {
              clearTimeout(timeout);
              reject(
                new Error("Background image failed to load from data URL")
              );
            };
            bgImg.src = dataURL;
          });
        } catch (err: any) {
          console.warn(
            "Failed to load background image for export:",
            err?.message
          );
          // Continue without background image
        }
      }

      // Get all element containers from the DOM to extract their positions and styles
      const elementContainers =
        canvasElement.querySelectorAll("[data-element-id]");
      const elementMap = new Map<
        string,
        { element: DesignElement; domElement: Element }
      >();

      // Map DOM elements to design elements
      elementContainers.forEach((container) => {
        const elementId = container.getAttribute("data-element-id");
        if (elementId) {
          const element = elements.find((el) => el.id === elementId);
          if (element) {
            elementMap.set(elementId, { element, domElement: container });
          }
        }
      });

      // Sort elements by zIndex
      const sortedElements = [...elements].sort((a, b) => a.zIndex - b.zIndex);

      // Draw each element
        for (const element of sortedElements) {
        const elementData = elementMap.get(element.id);
        if (!elementData) continue;

        ctx.save();

        // Set opacity
        ctx.globalAlpha = element.style.opacity ?? 1;

        // Calculate position and size scaled to actual image size
        // Scale independently on X and Y to preserve relative positioning
        const x = element.position.x * scaleX;
        const y = element.position.y * scaleY;
        const width = element.size.width * scaleX;
        const height = element.size.height * scaleY;

        // Calculate center for rotation
        const centerX = x + width / 2;
        const centerY = y + height / 2;

        // Apply rotation
        if (element.style.rotation) {
          ctx.translate(centerX, centerY);
          ctx.rotate((element.style.rotation * Math.PI) / 180);
          ctx.translate(-centerX, -centerY);
        }

        switch (element.type) {
          case "text":
            // Set text properties
            // Font scaling for export: scale from preset coordinates to actual image resolution
            // Canvas fonts may render differently than CSS, so we use scaleX with a multiplier
            // to ensure text appears at the correct size relative to the exported image
            const fontScale = scaleX * 1.5; // Multiply by 1.5 to account for canvas rendering differences
            const fontSize =
              (typeof element.style.fontSize === "number"
                ? element.style.fontSize
                : 24) * fontScale;
            const fontFamily =
              element.style.fontFamily ||
              "var(--store-font-family, 'Inter', sans-serif)";
            ctx.font = `${element.style.fontStyle || "normal"} ${element.style.fontWeight || "normal"} ${fontSize}px ${fontFamily}`;
            ctx.fillStyle = element.style.color || "#000000";
            ctx.textAlign =
              (element.style.textAlign as CanvasTextAlign) || "left";

            // Draw text
            const text = element.content || "Text";
            const lines = text.split("\n");
            const lineHeightValue =
              typeof element.style.lineHeight === "number"
                ? element.style.lineHeight
                : typeof element.style.lineHeight === "string"
                  ? parseFloat(element.style.lineHeight) || 1.5
                  : 1.5;
            const lineHeight = fontSize * lineHeightValue;

            // Use "top" baseline to match CSS text positioning (same as Product3DPreview)
            // This aligns text to the top of the text box, matching how CSS renders text
            ctx.textBaseline = "top";

            lines.forEach((line, index) => {
              // Position text at the element's Y position, accounting for line height
              const textY = y + index * lineHeight;
              ctx.fillText(line, x, textY);
            });
            break;

          case "shape":
            // Load shape image via backend to bypass CORS
            if (element.content) {
              try {
                const dataURL =
                  await productCustomizationApi.fetchImageAsDataURL(
                    element.content
                  );
                const shapeImg = new Image();
                await new Promise<void>((resolve) => {
                  const timeout = setTimeout(() => {
                    console.warn(
                      `Shape image load timeout: ${element.content}`
                    );
                    resolve();
                  }, 10000);
                  shapeImg.onload = () => {
                    clearTimeout(timeout);
                    try {
                      // Use full natural dimensions for best quality
                      const imgWidth = shapeImg.naturalWidth;
                      const imgHeight = shapeImg.naturalHeight;
                      // Draw at full resolution, scaling to element size
                      ctx.drawImage(
                        shapeImg,
                        0,
                        0,
                        imgWidth,
                        imgHeight,
                        x,
                        y,
                        width,
                        height
                      );
                    } catch (drawErr: any) {
                      console.warn(
                        `Failed to draw shape image: ${drawErr?.message}`
                      );
                    }
                    resolve();
                  };
                  shapeImg.onerror = () => {
                    clearTimeout(timeout);
                    console.warn(
                      `Failed to load shape image for export: ${element.content}`
                    );
                    resolve();
                  };
                  shapeImg.src = dataURL;
                });
              } catch (err: any) {
                console.warn(`Shape image export failed: ${err?.message}`);
                // Fallback: draw solid color shape
                ctx.fillStyle = element.style.backgroundColor || "#000000";
                if (element.style.borderRadius) {
                  const r = element.style.borderRadius * Math.min(scaleX, scaleY);
                  ctx.beginPath();
                  if (ctx.roundRect) {
                    ctx.roundRect(x, y, width, height, r);
                  } else {
                    ctx.moveTo(x + r, y);
                    ctx.lineTo(x + width - r, y);
                    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
                    ctx.lineTo(x + width, y + height - r);
                    ctx.quadraticCurveTo(
                      x + width,
                      y + height,
                      x + width - r,
                      y + height
                    );
                    ctx.lineTo(x + r, y + height);
                    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
                    ctx.lineTo(x, y + r);
                    ctx.quadraticCurveTo(x, y, x + r, y);
                  }
                  ctx.fill();
                } else {
                  ctx.fillRect(x, y, width, height);
                }
              }
            } else {
              // Draw solid color shape
              ctx.fillStyle = element.style.backgroundColor || "#000000";
              if (element.style.borderRadius) {
                const r = element.style.borderRadius * Math.min(scaleX, scaleY);
                ctx.beginPath();
                if (ctx.roundRect) {
                  ctx.roundRect(x, y, width, height, r);
                } else {
                  ctx.moveTo(x + r, y);
                  ctx.lineTo(x + width - r, y);
                  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
                  ctx.lineTo(x + width, y + height - r);
                  ctx.quadraticCurveTo(
                    x + width,
                    y + height,
                    x + width - r,
                    y + height
                  );
                  ctx.lineTo(x + r, y + height);
                  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
                  ctx.lineTo(x, y + r);
                  ctx.quadraticCurveTo(x, y, x + r, y);
                }
                ctx.fill();
              } else {
                ctx.fillRect(x, y, width, height);
              }

              // Draw border if present
              if (element.style.borderWidth && element.style.borderColor) {
                ctx.strokeStyle = element.style.borderColor;
                const avgScale = (scaleX + scaleY) / 2;
                ctx.lineWidth = (element.style.borderWidth || 2) * avgScale;
                if (element.style.borderRadius) {
                  const r = element.style.borderRadius * Math.min(scaleX, scaleY);
                  ctx.beginPath();
                  if (ctx.roundRect) {
                    ctx.roundRect(x, y, width, height, r);
                  } else {
                    ctx.moveTo(x + r, y);
                    ctx.lineTo(x + width - r, y);
                    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
                    ctx.lineTo(x + width, y + height - r);
                    ctx.quadraticCurveTo(
                      x + width,
                      y + height,
                      x + width - r,
                      y + height
                    );
                    ctx.lineTo(x + r, y + height);
                    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
                    ctx.lineTo(x, y + r);
                    ctx.quadraticCurveTo(x, y, x + r, y);
                  }
                  ctx.stroke();
                } else {
                  ctx.strokeRect(x, y, width, height);
                }
              }
            }
            break;

          case "frame":
            // Draw frame border
            ctx.strokeStyle = element.style.borderColor || "#000000";
            const frameAvgScale = (scaleX + scaleY) / 2;
            ctx.lineWidth = (element.style.borderWidth || 2) * frameAvgScale;
            if (element.style.borderRadius) {
              const r = element.style.borderRadius * Math.min(scaleX, scaleY);
              ctx.beginPath();
              if (ctx.roundRect) {
                ctx.roundRect(x, y, width, height, r);
              } else {
                ctx.moveTo(x + r, y);
                ctx.lineTo(x + width - r, y);
                ctx.quadraticCurveTo(x + width, y, x + width, y + r);
                ctx.lineTo(x + width, y + height - r);
                ctx.quadraticCurveTo(
                  x + width,
                  y + height,
                  x + width - r,
                  y + height
                );
                ctx.lineTo(x + r, y + height);
                ctx.quadraticCurveTo(x, y + height, x, y + height - r);
                ctx.lineTo(x, y + r);
                ctx.quadraticCurveTo(x, y, x + r, y);
              }
              ctx.stroke();
            } else {
              ctx.strokeRect(x, y, width, height);
            }
            break;
        }

        ctx.restore();
      }

      // Export as PNG at high quality (export at high DPI for maximum quality)
      await new Promise<void>((resolve, reject) => {
        try {
          exportCanvas.toBlob((blob) => {
            if (blob) {
              try {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `design-${Date.now()}.png`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                resolve();
              } catch (err: any) {
                reject(
                  new Error(
                    `Failed to download PNG: ${err?.message || "Unknown error"}`
                  )
                );
              }
            } else {
              reject(new Error("Failed to generate PNG blob"));
            }
          }, "image/png");
        } catch (err: any) {
          reject(err);
        }
      });
    } catch (error: any) {
      console.error("Error exporting PNG:", error);
      const errorMessage =
        error?.message || error?.toString() || "Unknown error occurred";
      alert(`Failed to export PNG: ${errorMessage}`);
    } finally {
      setExporting(false);
    }
  }, [elements, backgroundColor, backgroundImage, canvasSize]);

  return (
    <>
      {/* Close Confirmation Modal */}
      <Modal
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        title="Unsaved Changes"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowCloseConfirm(false)}
            >
              Cancel
            </Button>
            <Button variant="outline" onClick={handleCloseWithoutSaving}>
              Close Without Saving
            </Button>
            <Button variant="primary" onClick={handleSaveAndClose}>
              Save and Close
            </Button>
          </div>
        }
      >
        <p className="text-gray-700">
          You have unsaved changes to your design. If you close now, your
          changes will be lost.
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Would you like to save your design before closing?
        </p>
      </Modal>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="fixed inset-0 flex bg-gray-100 z-50">
          {/* Left Sidebar - Component Palette and Layers */}
          {!isPreviewMode && (
            <div className="w-64 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
              {/* Tabs */}
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab("components")}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    activeTab === "components"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Components
                </button>
                <button
                  onClick={() => setActiveTab("layers")}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    activeTab === "layers"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Layers
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden">
                {activeTab === "components" ? (
                  <div className="h-full flex flex-col">
                    <div className="pb-2 overflow-y-auto">
                      <ComponentPalette
                        onAddElement={addElement}
                        availableShapes={availableShapes}
                      />
                    </div>
                    {/* Properties Panel */}
                    {(selectedElement || selectedCanvas) && (
                      <>
                        <div className="border-t border-gray-200"></div>
                        <div className="flex-1 overflow-y-auto min-h-0">
                          <ElementPropertiesPanel
                            ref={propertiesPanelRef}
                            element={selectedElementData}
                            isCanvasSelected={selectedCanvas}
                            onUpdate={
                              selectedElementData
                                ? (updates: Partial<DesignElement>) =>
                                    updateElement(
                                      selectedElementData.id,
                                      updates
                                    )
                                : undefined
                            }
                            onDelete={
                              selectedElementData
                                ? () => deleteElement(selectedElementData.id)
                                : undefined
                            }
                            onSelectBackground={(url: string) => {
                              setBackgroundImage(url || undefined);
                            }}
                            selectedBackground={backgroundImage}
                            backgroundColor={backgroundColor}
                            onBackgroundColorChange={setBackgroundColor}
                            canvasSize={canvasSize}
                            backgroundImages={backgroundImages}
                            availableShapes={availableShapes}
                            allowedDesignerFonts={allowedDesignerFonts}
                          />
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="h-full overflow-y-auto">
                    <LayerTree
                      elements={elements}
                      selectedElement={selectedElement}
                      onSelectElement={setSelectedElement}
                      onDeleteElement={deleteElement}
                      onUpdateElement={updateElement}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main Canvas Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Toolbar */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Heading size="lg">Design Your Product</Heading>
                {hasUnsavedChanges && (
                  <FaExclamationCircle
                    className="w-4 h-4 text-amber-500"
                    title="You have unsaved changes"
                  />
                )}
              </div>

              <div className="flex items-center gap-2">
                {isPreviewMode ? (
                  // In preview mode, only show the button to switch back to edit mode
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsPreviewMode(false)}
                    className="flex items-center gap-2"
                  >
                    <FaEyeSlash className="w-4 h-4" />
                    Edit Mode
                  </Button>
                ) : (
                  // In edit mode, show all other buttons
                  <>
                    {/* Export button - only for store admins */}
                    {(user?.role === "admin" ||
                      user?.role === "store_admin") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportPNG}
                        disabled={elements.length === 0 || exporting}
                        className="flex items-center gap-2"
                      >
                        <FaDownload className="w-4 h-4" />
                        {exporting ? "Exporting..." : "Export PNG"}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsPreviewMode(true)}
                      className="flex items-center gap-2"
                    >
                      <FaCube className="w-4 h-4" />
                      3D Preview
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={elements.length === 0 || saving}
                      className={`flex items-center gap-2 ${hasUnsavedChanges ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                    >
                      <FaSave className="w-4 h-4" />
                      {saving ? "Saving..." : "Save Design"}
                      {hasUnsavedChanges && !saving && (
                        <FaExclamationCircle className="w-3 h-3 ml-1" />
                      )}
                    </Button>
                    {onCancel && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancel}
                        className="flex items-center gap-2"
                      >
                        <FaTimes className="w-4 h-4" />
                        Close
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Color Accuracy Notice */}
            <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
              <div className="flex items-center gap-2 text-blue-800 text-sm">
                <FaInfoCircle className="w-4 h-4 flex-shrink-0" />
                <span>
                  <strong>Color Accuracy Notice:</strong> The colors shown in
                  this preview may vary from the final printed product. Actual
                  colors may differ due to printing processes, materials, and
                  monitor calibration.
                </span>
              </div>
            </div>

            {/* Canvas or 3D Preview */}
            <div className="flex-1 overflow-auto bg-gray-50 p-8 flex items-center justify-center">
              {isPreviewMode ? (
                <div className="w-full h-full">
                  <Product3DPreview
                    elements={elements}
                    backgroundColor={backgroundColor}
                    backgroundImage={getOriginalBackgroundUrl(backgroundImage)} // Use original URL for admins
                    canvasSize={canvasSize}
                  />
                </div>
              ) : (
                <div className="bg-white shadow-lg rounded-lg p-4">
                  <DesignCanvas
                    ref={canvasRef}
                    baseImage={getOriginalBackgroundUrl(backgroundImage)} // Use original URL for admins
                    backgroundColor={backgroundColor}
                    elements={elements}
                    selectedElement={selectedElement}
                    selectedCanvas={selectedCanvas}
                    onElementSelect={handleElementSelect}
                    onCanvasSelect={handleCanvasSelect}
                    onElementUpdate={updateElement}
                    onElementDelete={deleteElement}
                    canvasSize={canvasSize}
                    isPreviewMode={isPreviewMode}
                    onElementDoubleClick={handleElementDoubleClick}
                    isAdmin={
                      !!(
                        user &&
                        (user.role === "admin" || user.role === "store_admin")
                      )
                    }
                    hasWatermark={
                      backgroundImage
                        ? backgroundImage.includes("watermarked")
                        : false
                    }
                    onRemoveWatermark={() => {
                      if (backgroundImage) {
                        const originalUrl =
                          getOriginalBackgroundUrl(backgroundImage);
                        if (originalUrl && originalUrl !== backgroundImage) {
                          backgroundImageManuallyChangedRef.current = true; // Mark as manually changed
                          setBackgroundImage(originalUrl);
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeDragId ? (
              <div className="bg-blue-500 text-white p-2 rounded shadow-lg">
                Dragging...
              </div>
            ) : null}
          </DragOverlay>
        </div>
      </DndContext>
    </>
  );
};

export default PrintDesigner;
